import {
  Form,
  FormInstance,
  Space,
  Typography,
  Button,
  Flex,
  Input,
} from "antd";
import { useTranslate } from "#src/hooks";
import { SpeakerHighIcon } from "@phosphor-icons/react";
import { useAntTheme } from "@/hooks/useAntTheme";
import {
  buildPrnAnchor,
  type WordToken,
} from "../../../../helpers/scriptHelpers";
import { useTextToSpeech } from "hooks/useTextToSpeech/";

export const PronunciationForm = ({
  form,
  onFinish,
  onCancel,
  token,
  sectionId,
  voiceId,
}: {
  form: FormInstance<{ pronunciation: string }>;
  onFinish: (values: { pronunciation: string }) => void;
  onCancel: () => void;
  token: WordToken;
  sectionId?: string;
  voiceId?: string;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  // TTS for pronunciation preview. sectionId lets the backend resolve the
  // section's current voice; cacheKey keeps each token's preview cached
  // separately without sending a fake slide id over the wire; voiceId busts
  // the cache when the section's avatar/voice changes.
  const { isLoading, isConnected, play, wsBusy } = useTextToSpeech({
    sectionId,
    cacheKey: `pronunciation-preview-${token.index}`,
    voiceId,
  });

  const pronunciationValue = Form.useWatch("pronunciation", form);

  const hasUnsavedChanges =
    !!pronunciationValue?.trim() &&
    pronunciationValue.trim() !== (token.pronunciation || "");
  const handlePlayClick = () => {
    const prnValue = pronunciationValue?.trim();
    if (!prnValue) return;
    play(buildPrnAnchor(prnValue, token.word));
  };

  const isPlayButtonDisabled =
    !pronunciationValue?.trim() || !isConnected || isLoading || wsBusy;

  return (
    <Form
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
      initialValues={{ pronunciation: token.pronunciation }}
      form={form}
      style={{ padding: 16 }}
    >
      <Space align="start" style={{ marginBottom: 24, width: "100%" }}>
        <Form.Item
          name="pronunciation"
          label={t(
            "components.templates.templatePreview.voiceoverNarration.pronunciation.label"
          ).toUpperCase()}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: t(
                "components.templates.templatePreview.voiceoverNarration.pronunciation.pleaseEnter"
              ),
            },
            {
              whitespace: true,
              message: t(
                "components.templates.templatePreview.voiceoverNarration.pronunciation.cannotBeEmpty"
              ),
            },
          ]}
          extra={
            <Typography.Text type="secondary">
              {t(
                "components.templates.templatePreview.voiceoverNarration.pronunciation.tip"
              )}
            </Typography.Text>
          }
        >
          <Input size="large" />
        </Form.Item>
        <Button
          icon={<SpeakerHighIcon />}
          size="large"
          disabled={isPlayButtonDisabled}
          loading={isLoading}
          onClick={handlePlayClick}
          style={{ borderColor: theme.colorBorder, marginTop: 29.5 }}
        />
      </Space>
      <FormButtons onCancel={onCancel} saveDisabled={!hasUnsavedChanges} />
      {hasUnsavedChanges && (
        <Typography.Text
          type="warning"
          style={{ marginTop: 8, display: "block", textAlign: "end" }}
        >
          {t(
            "components.templates.templatePreview.voiceoverNarration.editModal.youHaveUnsavedChanges"
          )}
        </Typography.Text>
      )}
    </Form>
  );
};

const FormButtons = ({
  onCancel,
  saveDisabled,
}: {
  onCancel: () => void;
  saveDisabled: boolean;
}) => {
  const t = useTranslate();
  return (
    <Flex style={{ width: "100%" }} justify="end" gap={8}>
      <Button size="large" onClick={onCancel}>
        {t("buttons.cancel")}
      </Button>
      <Button
        type="primary"
        size="large"
        htmlType="submit"
        disabled={saveDisabled}
      >
        {t("buttons.save")}
      </Button>
    </Flex>
  );
};
